import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { GlassPanel, Reveal } from '@/components/atoms';
import {
  ImportPromiseCopy,
  ImportPromiseCta,
  ImportPromiseProofs,
} from '@/components/molecules';
import { type ImportPromiseOverrides, useImportPromise } from '@/hooks/useImportPromise';

import { Section } from './Section';

/**
 * A wash of brand light from the top of the panel, so the card reads as lit
 * rather than as another grey box on a grey band.
 *
 * Painted as its own layer and not as the panel's `backgroundImage`, because
 * the panel already has one: `glassSurfaceSx` puts the specular sweep there,
 * and a second gradient would replace it and take the glass with it.
 *
 * Decorative and `aria-hidden`, under the content (`GlassPanel` sets
 * `isolation: isolate`, so this stacking stays inside the card).
 */
function PromiseGlow() {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        background:
          'radial-gradient(120% 80% at 50% 0%, rgba(var(--mui-palette-primary-mainChannel) / 0.16) 0%, rgba(var(--mui-palette-primary-mainChannel) / 0.04) 42%, transparent 72%)',
      }}
    />
  );
}

/**
 * The site-wide import promise (§27a) — the band that says what Kabex
 * actually is, on every page.
 *
 * It sits directly above `QuoteBand` on every page that has one, and the
 * order is the argument: this band is the *claim* (we import the car you
 * specify) and the quotation band is the *ask* (tell us which one). Putting
 * the ask first leaves the claim as an afterthought under it.
 *
 * Why it does not look like the bands around it: a message repeated on
 * nineteen pages stops being seen the moment it looks like page furniture. So
 * it is the one band on the site built as a lit card on a patterned ground —
 * centred, where every other section is left-aligned, and the only place the
 * brand red is used as light rather than as a control. Two bands in a row
 * being equally loud would also cancel each other out, which is the second
 * reason it is a glass card on a muted band and not a second dark slab above
 * the near-black one.
 *
 * All copy, defaults and the sheen trigger come from `useImportPromise`; this
 * file is structure only. The props exist so a page *could* reword it — see
 * `content/importPromise.ts` for why none do.
 */
export function ImportPromiseBand(overrides: ImportPromiseOverrides = {}) {
  const promise = useImportPromise(overrides);

  return (
    <Section tone="muted" pattern="grid" labelledBy={promise.titleId}>
      <GlassPanel
        tint="theme"
        sx={{
          overflow: 'hidden',
          px: { xs: 6, sm: 10, md: 12, lg: 16 },
          py: { xs: 10, sm: 12, md: 14 },
        }}
      >
        <PromiseGlow />
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 10, md: 12, lg: 16 }}
          sx={{ position: 'relative', zIndex: 1, alignItems: { md: 'center' } }}
        >
          <Stack spacing={7} sx={{ flex: { md: '0 1 44%' }, minWidth: 0 }}>
            <Reveal>
              <ImportPromiseCopy
                eyebrow={promise.eyebrow}
                title={promise.title}
                text={promise.text}
                titleId={promise.titleId}
                sheenRef={promise.sheenRef}
                playSheen={promise.playSheen}
              />
            </Reveal>

            <Reveal delay={140}>
              <ImportPromiseCta {...promise.cta} />
            </Reveal>
          </Stack>

          <ImportPromiseProofs proofs={promise.proofs} />
        </Stack>
      </GlassPanel>
    </Section>
  );
}
